import { z } from 'zod';

import i18n from '@/lib/i18n';
import { mainstreamCountrySchema } from '@/services/api/mainstream';
import { type Identity, type IdentityWithFallbacks, identityWithFallbacksSchema } from '@/services/api/profile';
import type { EmptyString } from '@/types';
import { toDatePartsFromString, toDateStringFromParts } from '@/utils/date';
import { dateOfBirthRefinement } from './refinements/date-of-birth';
import { postalCodeRefinement, postalCodeRefinementParams } from './refinements/postal-code';
import { taxIdentificationNumberRefinement } from './refinements/tax-identification-number';

export const TAX_RESIDENCES_MAX = 5;

const personalInformationTaxResidenceSchema = z
  .object({
    country: mainstreamCountrySchema(i18n.t('personalInformation.errors.taxResidenceCountry')),
    taxId: z.string(),
  })
  .superRefine(taxIdentificationNumberRefinement);

const personalInformationBaseSchema = z.object({
  firstName: z.string().min(1, {
    error: i18n.t('personalInformation.errors.firstName'),
  }),
  lastName: z.string().min(1, {
    error: i18n.t('personalInformation.errors.lastName'),
  }),
  citizenship: mainstreamCountrySchema(i18n.t('personalInformation.errors.citizenship')),
  citizenship2: mainstreamCountrySchema().optional(),
  dateOfBirth: z
    .object({
      day: z.string().regex(/^\d{1,2}$/, {
        error: i18n.t('personalInformation.errors.dateOfBirthDay'),
      }),
      month: z.string().regex(/^\d{1,2}$/, {
        error: i18n.t('personalInformation.errors.dateOfBirthMonth'),
      }),
      year: z.string().regex(/^\d{4}$/, {
        error: i18n.t('personalInformation.errors.dateOfBirthYear'),
      }),
    })
    .superRefine(dateOfBirthRefinement),
  townOfBirth: z.string().min(1, {
    error: i18n.t('personalInformation.errors.townOfBirth'),
  }),
  countryOfBirth: mainstreamCountrySchema(i18n.t('personalInformation.errors.countryOfBirth')),
  countryOfResidency: mainstreamCountrySchema(i18n.t('personalInformation.errors.countryOfResidency')),
  address: z.string().min(1, {
    error: i18n.t('personalInformation.errors.address'),
  }),
  address2: z.string().optional(),
  // Requiredness is country-driven — see postalCodeRefinement.
  postCode: z.string({
    error: i18n.t('personalInformation.errors.postCode'),
  }),
  city: z.string().min(1, {
    error: i18n.t('personalInformation.errors.city'),
  }),
  taxId: z.string().optional(),
  taxResidences: z
    .array(personalInformationTaxResidenceSchema)
    .min(1)
    .max(TAX_RESIDENCES_MAX)
    .superRefine((taxResidences, context) => {
      taxResidences.forEach(({ country }, index) => {
        if (taxResidences.findIndex((taxResidence) => taxResidence.country === country) !== index) {
          context.addIssue({ code: 'custom', message: i18n.t('personalInformation.errors.taxResidenceCountryDuplicate'), path: [index, 'country'] });
        }
      });
    }),
  areaCode: z.string().min(1, {
    error: i18n.t('personalInformation.errors.areaCode'),
  }),
  phone: z.string().min(1, {
    error: i18n.t('personalInformation.errors.mobilePhone'),
  }),
});

export const personalInformationSchema = personalInformationBaseSchema.refine(postalCodeRefinement, postalCodeRefinementParams);

export type PersonalInformation = z.infer<typeof personalInformationSchema>;

export type PersonalInformationDefaults = Omit<PersonalInformation, 'dateOfBirth'> & {
  dateOfBirth: {
    [K in keyof PersonalInformation['dateOfBirth']]: PersonalInformation['dateOfBirth'][K] | EmptyString;
  };
};

export type PersonalInformationOrDefaults = PersonalInformation | PersonalInformationDefaults;

export const personalInformationCountryOfResidencySchema = personalInformationBaseSchema.pick({
  countryOfResidency: true,
});

export type PersonalInformationCountryOfResidency = z.infer<typeof personalInformationCountryOfResidencySchema>;

export const personalInformationAboutYouSchema = personalInformationBaseSchema.pick({
  firstName: true,
  lastName: true,
  dateOfBirth: true,
  citizenship: true,
  citizenship2: true,
  townOfBirth: true,
  countryOfBirth: true,
});

export type PersonalInformationAboutYou = z.infer<typeof personalInformationAboutYouSchema>;

export const personalInformationHomeAddressSchema = personalInformationBaseSchema
  .pick({
    // countryOfResidency isn't rendered in this step, but is required for the refinement
    countryOfResidency: true,
    address: true,
    address2: true,
    postCode: true,
    city: true,
  })
  .refine(postalCodeRefinement, postalCodeRefinementParams);

export type PersonalInformationHomeAddress = z.infer<typeof personalInformationHomeAddressSchema>;

export const personalInformationMobilePhoneSchema = personalInformationBaseSchema.pick({
  areaCode: true,
  phone: true,
});

export type PersonalInformationMobilePhone = z.infer<typeof personalInformationMobilePhoneSchema>;

export const personalInformationTaxResidencySchema = personalInformationBaseSchema.pick({
  taxResidences: true,
});

export type PersonalInformationTaxResidency = z.infer<typeof personalInformationTaxResidencySchema>;

export const identityToPersonalInformationWithDefaults = identityWithFallbacksSchema.transform((identity: Identity | IdentityWithFallbacks): PersonalInformationOrDefaults => {
  return {
    firstName: identity.firstName,
    lastName: identity.lastName,
    citizenship: identity.citizenship,
    citizenship2: identity.citizenship2,
    dateOfBirth: toDatePartsFromString(identity.birthInfo.date),
    townOfBirth: identity.birthInfo.locality,
    countryOfBirth: identity.birthInfo.country,
    countryOfResidency: identity.addressOfResidency.country,
    address: identity.addressOfResidency.addressLines[0] || '',
    address2: identity.addressOfResidency.addressLines[1] || '',
    postCode: identity.addressOfResidency.postalCode,
    city: identity.addressOfResidency.locality,
    taxId: identity.taxId || '',
    taxResidences: identity.taxResidency?.residences.map(({ country, taxId }) => ({ country, taxId: taxId ?? '' })) ?? [{ country: '', taxId: '' }],
    areaCode: identity.phoneNumber.areaCode,
    phone: identity.phoneNumber.mobilePhone,
  };
});

export const personalInformationToIdentity = personalInformationSchema.transform(
  (data): Identity => ({
    firstName: data.firstName,
    lastName: data.lastName,
    citizenship: data.citizenship,
    citizenship2: data.citizenship2,
    birthInfo: {
      date: toDateStringFromParts(data.dateOfBirth),
      locality: data.townOfBirth,
      country: data.countryOfBirth,
    },
    addressOfResidency: {
      addressLines: [data.address, ...(data.address2 ? [data.address2] : [])].map((line) => line.replace(/,/g, '')),
      country: data.countryOfResidency,
      locality: data.city,
      postalCode: data.postCode,
    },
    phoneNumber: {
      areaCode: data.areaCode,
      mobilePhone: data.phone,
    },
    taxId: data.taxId,
    taxResidency: {
      residences: data.taxResidences.map(({ country, taxId }) => ({ country, ...(taxId && { taxId }) })),
    },
  }),
);
